/* eslint-disable @atlaskit/ui-styling-standard/no-nested-selectors */
/**
 * Adding the eslint disable rule above, as we will be updating the nested css in this file in phases
 */

import React, { useRef } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { AtlassianIcon } from "@atlaskit/logo";
import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { ButtonColor } from "@/components/adg4/button/src";
import { useLocalizedHref } from "@/libraries/web-framework";
import { ProductKeyTypes } from "@/libraries/wpl/foundations/src";

import { useMore } from "./hooks/use-more";
import { MobileNavLinksContainerInfo } from "./mobile-nav/mobile-nav-links-container";
import {
    MobileProductPageNav,
    AdditionalMenuPageObjects
} from "./mobile-nav/mobile-product-page-nav";
import GlobalNavLinksContainer, {
    GlobalNavLinkContentProps,
    GlobalNavProvider
} from "./nav-links-container";
import SearchAndProfile from "./nav-search-and-profile";
import SearchBar from "./search-bar";

const mobileWrapperCss = css({
    "display": "block",
    "position": "sticky",
    "insetBlockStart": "0",
    "zIndex": "5",

    ".product-page-mobile-nav": {
        display: "none"
    },

    [media.below.md]: {
        ".product-page-desktop-nav": {
            display: "none"
        },

        ".product-page-mobile-nav": {
            display: "block"
        }
    }
});

const atlasIconContainerStyle = css({
    height: "68px",
    width: "68px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#1868DB"
});

const atlasIconStyle = css({
    height: "56px",
    width: "56px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
});

const jsmLogoStyle = css({
    ".productpage-subnav-bar": {
        ".productpage-subnav-logo-and-links-container": {
            ".productpage-subnav-product-logo": {
                "a.navigation-bar-logo-link": {
                    svg: {
                        height: "24px"
                    }
                }
            }
        }
    }
});

const productPageSubNavStyle = css({
    "display": "flex",
    "flexDirection": "row",
    "justifyContent": "flex-start",
    "position": "relative",
    "alignItems": "center",
    "backgroundColor": "#ffffff",
    "flex": 1,
    "zIndex": 5,
    "width": "100%",
    "height": "68px",

    ".productpage-subnav-bar": {
        "display": "flex",
        "flex": 1,
        "flexDirection": "row",
        "position": "relative",
        "justifyContent": "center",
        "alignItems": "center",
        "listStyle": "none",
        "padding": 0,

        [media.above.sm]: {
            maxWidth: "1152px"
        },

        ".productpage-subnav-logo-and-links-container": {
            "display": "flex",
            "flexDirection": "row",
            "alignItems": "flex-end",
            "flex": 1,

            ".navigation-links": {
                marginBlockEnd: "4px"
            },

            ".productpage-subnav-product-logo": {
                "display": "flex",
                "justifyContent": "center",
                "alignItems": "center",
                "paddingInlineEnd": "44px",

                "a.navigation-bar-logo-link": {
                    "display": "flex",
                    "justifyContent": "center",
                    "alignItems": "end",
                    "> div": {
                        display: "flex",
                        alignItems: "end"
                    },
                    "span": {
                        height: "24px"
                    },
                    "svg": {
                        height: "26px"
                    }
                }
            },
            ".productpage-subnav-bar-links > .navigation-links": {
                "display": "flex",
                "flexDirection": "row",
                "alignItems": "flex-end",
                "justifyContent": "flex-start",
                "position": "relative",
                "listStyle": "none",
                "padding": "0",
                "flexBasis": "0%",
                "flexShrink": "1",
                "flexGrow": "1",

                "> li.nav-link": {
                    "> a.nav-link-anchor": {
                        "alignItems": "center",

                        "display": "flex",
                        "textDecoration": "none",
                        "&.isActive": {
                            color: "#1868DB"
                        }
                    },
                    [media.above.md]: {
                        "> a.nav-link-anchor": {
                            borderRadius: "3px",
                            paddingInlineStart: token("space.150"),
                            paddingInlineEnd: token("space.150"),
                            textDecoration: "none",
                            marginInlineEnd: "2px"
                        }
                    }
                }
            }
        }
    },

    ".search-bar": {
        width: "100%",

        [media.above.sm]: {
            maxWidth: "1152px"
        }
    }
});

export type BasedProductPageSubNavProps = {
    readonly productLogo: JSX.Element;
    readonly productLogoUrl: string;
    readonly productLogoAriaLabel: string;
    readonly productKey?: ProductKeyTypes;
    readonly productPageNavLinks: GlobalNavLinkContentProps[];
    readonly isShowSearchBarAndProfileContainer?: boolean;
    readonly isShowTryNowLink?: boolean;
    readonly tryNowCTAButtonBackgroundColor?: ButtonColor;
    readonly isShowSearchButton?: boolean;
    readonly navigationTrynowCtaText?: string;
    readonly navigationTrynowCtaUrl?: string;
    readonly navigationTrynowCtaOnClick?: (e: React.MouseEvent) => void;
};

export type ProductPageSubNavProps = BasedProductPageSubNavProps & {
    readonly mobileLogo?: JSX.Element;
    readonly mobileLinksSections: MobileNavLinksContainerInfo[];
    readonly isIncludeMobileCTA?: boolean;
    readonly mobileCtaText?: string;
    readonly mobileCtaUrl?: string;
    readonly mobileCtaOnClick?: (e: React.MouseEvent) => void;
    readonly isIncludeMobileAtlassianLogo?: boolean;
    readonly additionalMenuPageObjectsArray?: AdditionalMenuPageObjects[];
    readonly isSurfaceTemplatesMenuInJsmProductTour?: boolean;
};

type ProductPageSubNavSetStateProps = {
    readonly isOpenSearch: boolean;
    readonly setIsOpenSearch: React.Dispatch<React.SetStateAction<boolean>>;
};

type ProductPageSubNavLinksProps = BasedProductPageSubNavProps &
    ProductPageSubNavSetStateProps;

const ProductPageSubNavLinks = ({
    productLogo,
    productLogoUrl,
    productLogoAriaLabel,
    productKey,
    productPageNavLinks,
    isShowSearchBarAndProfileContainer = true,
    isShowTryNowLink = true,
    tryNowCTAButtonBackgroundColor,
    isShowSearchButton = true,
    isOpenSearch,
    setIsOpenSearch,
    navigationTrynowCtaText = "Get it free",
    navigationTrynowCtaUrl = "/try",
    navigationTrynowCtaOnClick
}: ProductPageSubNavLinksProps) => {
    const linksRef = useRef<HTMLUListElement | null>(null);
    const searchAndProfileRef = useRef<HTMLDivElement | null>(null);

    const { showMoreItems, shouldShowMoreItems, maxItems } = useMore({
        leftElementRef: linksRef,
        rightElementRef: searchAndProfileRef,
        initialMaxItems: productPageNavLinks.length
    });

    return (
        <div
            css={css({
                display: "flex",
                flex: 1,
                justifyContent: "center",
                paddingInlineStart: "68px",
                paddingInlineEnd: "136px"
            })}
        >
            <div className="productpage-subnav-bar">
                <div
                    className="productpage-subnav-logo-and-links-container"
                    style={
                        isOpenSearch
                            ? { alignItems: "center" }
                            : { alignItems: "flex-end" }
                    }
                >
                    <div className="productpage-subnav-product-logo">
                        <a
                            className="navigation-bar-logo-link"
                            href={productLogoUrl}
                            aria-label={productLogoAriaLabel}
                            data-event="clicked"
                            data-event-component="linkButton"
                            data-event-container="productPageNav"
                            data-product-key={productKey ?? "jira"}
                        >
                            {productLogo}
                        </a>
                    </div>
                    {isOpenSearch ? (
                        <SearchBar setIsOpenSearch={setIsOpenSearch} />
                    ) : (
                        <GlobalNavProvider
                            itemList={productPageNavLinks.map(
                                (link) => link.globalLinksKey
                            )}
                        >
                            <GlobalNavLinksContainer
                                globalLinksProps={productPageNavLinks}
                                globalLinksRef={linksRef}
                                showMore={shouldShowMoreItems}
                                setShowMore={showMoreItems}
                                maxLinks={maxItems}
                            />
                        </GlobalNavProvider>
                    )}
                </div>
                {!isOpenSearch &&
                    isShowSearchBarAndProfileContainer &&
                    !shouldShowMoreItems && (
                        <SearchAndProfile
                            isShowTryNowLink={isShowTryNowLink}
                            ctaButtonColor={tryNowCTAButtonBackgroundColor}
                            productKey={productKey}
                            isShowSearchButton={isShowSearchButton}
                            setIsOpenSearch={setIsOpenSearch}
                            navigationTrynowCtaText={navigationTrynowCtaText}
                            navigationTrynowCtaUrl={navigationTrynowCtaUrl}
                            navigationTrynowCtaOnClick={
                                navigationTrynowCtaOnClick
                            }
                            searchAndProfileRef={searchAndProfileRef}
                        />
                    )}
            </div>
        </div>
    );
};

export const ProductPageSubNav = ({
    productLogo,
    productLogoUrl,
    productLogoAriaLabel,
    productKey,
    productPageNavLinks,
    isShowSearchBarAndProfileContainer = true,
    isShowTryNowLink = true,
    tryNowCTAButtonBackgroundColor,
    isShowSearchButton = true,
    navigationTrynowCtaText = "Get it free",
    navigationTrynowCtaUrl = "/try",
    navigationTrynowCtaOnClick,
    mobileLogo,
    mobileLinksSections,
    isIncludeMobileCTA = true,
    mobileCtaText = "Get it free",
    mobileCtaUrl = "/software/jira/free",
    mobileCtaOnClick,
    isIncludeMobileAtlassianLogo = true,
    additionalMenuPageObjectsArray = [],
    isSurfaceTemplatesMenuInJsmProductTour
}: ProductPageSubNavProps) => {
    const intl = useIntl();
    const [isOpenSearch, setIsOpenSearch] = React.useState(false);

    return (
        <div css={mobileWrapperCss} className="product-page-nav-container">
            <div
                className="product-page-desktop-nav"
                css={[
                    productPageSubNavStyle,
                    isSurfaceTemplatesMenuInJsmProductTour && jsmLogoStyle
                ]}
            >
                <div css={atlasIconContainerStyle}>
                    <a
                        css={atlasIconStyle}
                        href={useLocalizedHref("/")}
                        aria-label={intl.formatMessage({
                            id: "productPageNav.logo-label",
                            defaultMessage: "Atlassian logo"
                        })}
                        data-event="clicked"
                        data-event-component="linkButton"
                        data-event-container="productPageSubNav"
                        data-product-key="home"
                    >
                        <AtlassianIcon size="medium" appearance="inverse" />
                    </a>
                </div>
                <ProductPageSubNavLinks
                    productLogo={productLogo}
                    productLogoUrl={productLogoUrl}
                    productLogoAriaLabel={productLogoAriaLabel}
                    productKey={productKey}
                    productPageNavLinks={productPageNavLinks}
                    isShowSearchBarAndProfileContainer={
                        isShowSearchBarAndProfileContainer
                    }
                    isShowTryNowLink={isShowTryNowLink}
                    tryNowCTAButtonBackgroundColor={
                        tryNowCTAButtonBackgroundColor
                    }
                    isShowSearchButton={isShowSearchButton}
                    isOpenSearch={isOpenSearch}
                    setIsOpenSearch={setIsOpenSearch}
                    navigationTrynowCtaText={navigationTrynowCtaText}
                    navigationTrynowCtaUrl={navigationTrynowCtaUrl}
                    navigationTrynowCtaOnClick={navigationTrynowCtaOnClick}
                />
            </div>
            <div className="product-page-mobile-nav">
                <MobileProductPageNav
                    productKey={productKey as ProductKeyTypes}
                    mobileLogo={mobileLogo}
                    mobileLogoAriaLabel={productLogoAriaLabel}
                    mobileLogoHref={productLogoUrl}
                    mobileLogoDataEventContainer={"productPageNavLogo"}
                    mobileLinksSections={mobileLinksSections}
                    isIncludeCTA={isIncludeMobileCTA}
                    ctaText={mobileCtaText}
                    ctaUrl={mobileCtaUrl}
                    ctaOnClick={mobileCtaOnClick}
                    isIncludeAtlassianLogo={isIncludeMobileAtlassianLogo}
                    isShowTryNowLink={isShowTryNowLink}
                    additionalMenuPageObjectsArray={
                        additionalMenuPageObjectsArray
                    }
                />
            </div>
        </div>
    );
};
